import React from "react";
import { navigate } from "@reach/router";
import { Formik } from "formik";
import * as XLSX from "xlsx";
import styled from "styled-components";
import { CustomFileUpload } from "../../../common/FormInputs";
import { CustomInputField } from "../../../common/FormInputs";
import { Autocomplete } from "@material-ui/lab";
import { Popper } from "@material-ui/core";
import useLoader from "../../../../hooks/useLoader";
import { useSnackbar } from "notistack";
import AWSService from "../../../../services/AWSService";
import CaseService from "../../../../services/CaseService";
import { getErrorMessage, getFileName } from "../../../../helpers/functions";
import { splitFileValidationSchema } from "../../../../helpers/validationSchema";
import COLORS from "../../../../assets/Colors";
import CustomSelect from "./CustomSelect";
import theme from "../../../../assets/theme";
import { ButtonGroup, BackButton, SubmitButton } from "./styles";

const CustomPopper = (props) => {
  const anchorEl = props.anchorEl;
  const width = anchorEl ? anchorEl.clientWidth : undefined;

  return (
    <Popper
      {...props}
      style={{
        ...props.style,
        width: width || undefined,
        zIndex: 1300,
      }}
      placement="bottom-start"
      modifiers={{
        flip: { enabled: true },
        preventOverflow: { enabled: true, boundariesElement: "scrollParent" },
      }}
    />
  );
};

const UPLOAD_TEMPLATES = [
  {
    name: "contractNo",
    label: "Upload Excel File",
    fileName: "excel",
    accept: ".xlsx",
    types: [
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      "application/vnd.ms-excel",
      "text/csv",
    ],
  },
  {
    name: "pdfFile",
    label: "Upload PDF Document",
    fileName: "pdf",
    accept: ".pdf",
    types: ["application/pdf"],
  },
];

const VALID_FILE_TYPES = [
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  "application/vnd.ms-excel",
  "text/csv",
  "application/pdf",
];

const CONTRACT_HEADERS = [
  "CONTRACT NUMBER",
  "Contract No",
  "Contract Number",
  "contract no",
];

const SplitAndUpload = ({ menuItems }) => {
  const { setLoader } = useLoader();
  const { enqueueSnackbar } = useSnackbar();

  async function s3Upload(file, field, setFieldValue, accept, fileName) {
    if (!file) return;

    try {
      setLoader({ state: true, message: `Uploading ${file.name}...` });

      if (!VALID_FILE_TYPES.includes(file?.type)) {
        throw new Error("Invalid File Type");
      }

      if (accept === ".xlsx") {
        const contractNumbers = await readExcelFile(file);
        setFieldValue(field, contractNumbers);
        if (contractNumbers?.length) {
          setFieldValue(fileName, file.name);
          enqueueSnackbar(
            `Excel processed: ${contractNumbers.length} contracts found`,
            {
              variant: "success",
            },
          );
        }
      } else if (accept === ".pdf") {
        const payload = { key: file.name };
        const s3Response = await AWSService.getS3URL(payload);

        if (s3Response.url) {
          const res = await AWSService.uploadToS3(s3Response.url, file);
          setFieldValue(field, res);
          enqueueSnackbar("PDF uploaded successfully", { variant: "success" });
        }
      }
    } catch (err) {
      enqueueSnackbar(err?.message || "Upload failed", { variant: "error" });
    } finally {
      setLoader({ state: false });
    }
  }

  const readExcelFile = async (file) => {
    try {
      const arrayBuffer = await file.arrayBuffer();
      const workbook = XLSX.read(arrayBuffer, { type: "array" });
      const sheetName = workbook.SheetNames[0];
      const sheet = workbook.Sheets[sheetName];
      const data = XLSX.utils.sheet_to_json(sheet, { header: 1 });

      const headerRow = data[0];
      const contractNumberIndex = headerRow.findIndex((header) =>
        CONTRACT_HEADERS.includes(header?.trim()),
      );

      if (contractNumberIndex === -1) {
        throw new Error("Contract number column not found in Excel file");
      }

      const contractNumbers = data
        .slice(1)
        .map((row) => row[contractNumberIndex])
        .filter(Boolean);

      if (contractNumbers.length === 0) {
        throw new Error("No contract numbers found in Excel file");
      }

      return contractNumbers;
    } catch (err) {
      throw err;
    }
  };

  async function onFormSubmit(values, { resetForm }) {
    try {
      setLoader({ state: true, message: "Processing split and upload..." });

      const payload = {
        partyId: values.partyId,
        pdfFile: values.pdfFile,
        contractNo: values.contractNo.map(String),
        noticeCategory: values.noticeCategory,
      };

      const response = await CaseService.splitUpload(payload);

      if (response?.message) {
        enqueueSnackbar(response.message, { variant: "success" });
      }

      resetForm();
    } catch (error) {
      const message = getErrorMessage(error);
      enqueueSnackbar(message, { variant: "error" });
    } finally {
      setLoader({ state: false });
    }
  }

  return (
    <PageWrapper>
      <Card>
        <Formik
          initialValues={{
            excel: "",
            contractNo: "",
            noticeCategory: "",
            pdfFile: "",
            partyId: "",
          }}
          validationSchema={splitFileValidationSchema}
          enableReinitialize
          onSubmit={onFormSubmit}
          validateOnBlur
          validateOnChange
        >
          {({ values, errors, touched, handleSubmit, setFieldValue }) => (
            <form onSubmit={handleSubmit}>
              <Section>
                <FormGrid>
                  <div>
                    <Label>Notice Category Name</Label>
                    <StyledAutocomplete
                      id="notice-category-autocomplete"
                      size="small"
                      options={menuItems}
                      groupBy={(option) => option.category}
                      getOptionLabel={(option) => option.name}
                      PopperComponent={CustomPopper}
                      renderInput={(params) => (
                        <div ref={params.InputProps.ref}>
                          <CustomInputField
                            {...params}
                            variant="outlined"
                            placeholder="Select notice category..."
                            style={{
                              backgroundColor: "#ffffff",
                              borderRadius: "10px",
                            }}
                          />
                        </div>
                      )}
                      renderGroup={(params) => (
                        <div style={{ padding: "8px 0" }}>
                          <div
                            style={{
                              padding: "10px 16px",
                              fontWeight: "700",
                              fontSize: "11px",
                              color: "#0f766e",
                              textTransform: "uppercase",
                              letterSpacing: "0.8px",
                              backgroundColor: "#f0fdf4",
                              borderRadius: "6px",
                              margin: "4px 8px",
                            }}
                          >
                            {params.group}
                          </div>
                          <div
                            style={{
                              padding: "4px 0",
                              color: COLORS.COLOR_DARK,
                              fontFamily: theme.fonts.primaryFontSemiBold,
                            }}
                          >
                            {params.children}
                          </div>
                        </div>
                      )}
                      noOptionsText={
                        <div
                          style={{
                            color: COLORS.INPUT_LABEL,
                            fontFamily: "openSans-SemiBold, sans-serif",
                            fontSize: "14px",
                            padding: "8px",
                          }}
                        >
                          No document type found
                        </div>
                      }
                      onChange={(event, newValue) =>
                        setFieldValue("noticeCategory", newValue?.name || "")
                      }
                    />
                  </div>

                  <CustomSelect
                    name="partyId"
                    trigger={true}
                    getNoticeTemplates={() => {}}
                    errors={errors}
                    touched={touched}
                    setFieldValue={setFieldValue}
                    menuItems={menuItems}
                  />
                </FormGrid>
              </Section>

              <Section>
                <FormGrid>
                  {UPLOAD_TEMPLATES.map((upload) => (
                    <div key={upload.name}>
                      <Label>
                        {upload.label}
                        <span className="required-star"> *</span>
                      </Label>
                      <CustomFileUpload
                        error={
                          upload.accept === ".xlsx"
                            ? touched[upload.fileName] &&
                              errors[upload.fileName]
                            : touched[upload.name] && errors[upload.name]
                        }
                        setFile={(file) =>
                          s3Upload(
                            file,
                            upload.name,
                            setFieldValue,
                            upload.accept,
                            upload.fileName,
                          )
                        }
                        style={{ width: "100%", borderRadius: "12px" }}
                        accept={upload.accept}
                        file={{
                          name:
                            upload.fileName === "excel"
                              ? values[upload.fileName]
                              : upload.fileName === "pdf"
                                ? getFileName(values[upload.name])
                                : "",
                        }}
                      />
                    </div>
                  ))}
                </FormGrid>
              </Section>

              <ButtonGroup>
                <BackButton
                  type="button"
                  onClick={() => navigate("/dashboard/case-manager")}
                >
                  Back
                </BackButton>
                <SubmitButton onClick={handleSubmit}>Submit</SubmitButton>
              </ButtonGroup>
            </form>
          )}
        </Formik>
      </Card>
    </PageWrapper>
  );
};

export default SplitAndUpload;

export const PageWrapper = styled.div`
  min-height: 100vh;
  padding: 32px 16px;

  @media (max-width: 768px) {
    padding: 20px 12px;
  }

  @media (max-width: 480px) {
    padding: 16px 8px;
  }
`;

export const Card = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  background: #ffffff;
  border-radius: 20px;
  box-shadow:
    0 20px 25px -5px rgba(0, 0, 0, 0.1),
    0 10px 10px -5px rgba(0, 0, 0, 0.04);
  padding: 40px;
  border: 1px solid #e5e7eb;

  @media (max-width: 768px) {
    padding: 24px;
    border-radius: 16px;
    box-shadow:
      0 10px 15px -3px rgba(0, 0, 0, 0.1),
      0 4px 6px -2px rgba(0, 0, 0, 0.05);
  }

  @media (max-width: 480px) {
    padding: 16px;
    border-radius: 12px;
  }
`;

export const Section = styled.section`
  background: linear-gradient(135deg, #f9fafb 0%, #ffffff 100%);
  padding: 28px;
  border-radius: 16px;
  margin-bottom: 24px;
  border: 1px solid #e5e7eb;
  transition: box-shadow 0.3s ease;

  &:hover {
    box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
  }

  @media (max-width: 768px) {
    padding: 20px;
    border-radius: 12px;
    margin-bottom: 20px;
  }

  @media (max-width: 480px) {
    padding: 16px;
    border-radius: 10px;
    margin-bottom: 16px;
  }
`;

export const Label = styled.label`
  display: block;
  margin-bottom: 10px;
  font-size: 12px;
  color: ${COLORS.INPUT_LABEL};
  font-family: openSans-SemiBold, sans-serif;

  @media (max-width: 480px) {
    font-size: 11px;
    margin-bottom: 8px;
  }

  .required-star {
    color: #ef4444;
  }
`;

export const FormGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 24px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 20px;
  }

  @media (max-width: 480px) {
    gap: 16px;
  }
`;

export const StyledAutocomplete = styled(Autocomplete)`
  & .MuiAutocomplete-listbox {
    color: #293461;
    font-family: openSans-SemiBold, sans-serif;
    font-size: 14px;
    background-color: #ffffff;
    border: 1px solid #e5e7eb;
    border-radius: 12px;
    box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1);
    max-height: 300px;
    padding: 8px 0;

    & .MuiAutocomplete-option {
      padding: 12px 16px;
      transition: all 0.2s ease;

      &:hover {
        background-color: #f0fdf4;
      }

      &[aria-selected="true"],
      &[data-focus="true"] {
        background-color: #ecfdf5;
      }
    }

    @media (max-width: 480px) {
      font-size: 13px;
      max-height: 250px;

      & .MuiAutocomplete-option {
        padding: 10px 14px;
      }
    }
  }

  & .MuiAutocomplete-noOptions {
    font-family: openSans-SemiBold, sans-serif;
    color: #6b7280;
    padding: 16px;
    font-size: 14px;

    @media (max-width: 480px) {
      padding: 12px;
      font-size: 13px;
    }
  }

  & .MuiInputBase-root {
    @media (max-width: 480px) {
      font-size: 14px;
    }
  }
`;

export const StyledForm = styled.form`
  display: flex;
  flex-direction: column;
`;

export const FormLabel = styled.label`
  display: block;
  margin-bottom: 10px;
  font-size: 12px;
  color: ${COLORS.INPUT_LABEL};
  font-family: openSans-SemiBold, sans-serif;

  @media (max-width: 480px) {
    font-size: 11px;
    margin-bottom: 8px;
  }

  .required-star {
    color: #ef4444;
  }
`;

export const StyledFormElementsWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;

  @media (max-width: 768px) {
    gap: 16px;
  }

  @media (max-width: 480px) {
    gap: 14px;
  }
`;
